import { useState, useEffect } from "react";
import { Progress } from "@/components/ui/progress";
import { User, Users, Target, Lightbulb, ClipboardCheck, Rocket, Loader2, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import AspiringLayout from "@/components/AspiringLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JOURNEY_STEPS = [
  { 
    key: "profile", 
    label: "Your Profile", 
    description: "Define your role, skills & unique edge",
    path: "/aspiring/profile", 
    icon: User,
    color: "from-blue-400 to-blue-600"
  },
  { 
    key: "cofounder", 
    label: "Your Cofounder", 
    description: "Find or describe your co-founder",
    path: "/aspiring/cofounder", 
    icon: Users,
    color: "from-purple-400 to-purple-600"
  },
  { 
    key: "idea", 
    label: "Your Idea", 
    description: "Explore market opportunities",
    path: "/aspiring/idea", 
    icon: Target,
    color: "from-emerald-400 to-emerald-600"
  },
  { 
    key: "solution", 
    label: "Your Solution", 
    description: "Define your technology approach",
    path: "/aspiring/solution", 
    icon: Lightbulb,
    color: "from-amber-400 to-orange-500"
  },
  { 
    key: "assessment", 
    label: "Assessment", 
    description: "AI-powered readiness check",
    path: "/aspiring/assessment", 
    icon: ClipboardCheck,
    color: "from-rose-400 to-rose-600"
  },
  { 
    key: "launch", 
    label: "Making the Jump", 
    description: "Resources & next steps",
    path: "/aspiring/launch", 
    icon: Rocket,
    color: "from-indigo-400 to-indigo-600"
  },
];

function DashboardContent({ user }) {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({});
  const [progress, setProgress] = useState(0);
  const [stepStatus, setStepStatus] = useState({});

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/aspiring/profile`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile || {});
        calculateProgress(data.profile || {});
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const calculateProgress = (p) => {
    const status = {
      profile: !!(p.preferred_role && p.skills?.length > 0),
      cofounder: p.no_cofounder || !!(p.cofounder_name),
      idea: p.no_idea === false && !!(p.therapeutic_area || p.market_opportunity),
      solution: !!(p.solution_description),
      assessment: !!(p.assessment),
      launch: !!(p.current_employment || p.next_steps)
    };
    setStepStatus(status);
    
    const completed = Object.values(status).filter(Boolean).length;
    setProgress(Math.round((completed / 6) * 100));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const firstName = user?.name?.split(" ")[0] || "Founder";
  const completedCount = Object.values(stepStatus).filter(Boolean).length;

  return (
    <div className="max-w-5xl mx-auto" data-testid="aspiring-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
          Welcome, {firstName}!
        </h1>
        <p className="text-slate-600 mt-1">Your journey to starting a biotech company starts here</p>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Your Progress</h2>
            <p className="text-sm text-slate-500">{completedCount} of 6 sections completed</p>
          </div>
          <div className="text-3xl font-bold text-amber-600">{progress}%</div>
        </div>
        <Progress value={progress} className="h-3" />
      </div>

      {/* Bubble Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {JOURNEY_STEPS.map((step) => {
          const Icon = step.icon;
          const done = stepStatus[step.key];
          
          return (
            <Link 
              key={step.key} 
              to={step.path}
              data-testid={`bubble-${step.key}`}
              className="group"
            >
              <div className={`
                relative overflow-hidden rounded-2xl border-2 p-6 transition-all duration-300
                ${done 
                  ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-md' 
                  : 'bg-white border-slate-200 hover:border-amber-300 hover:shadow-lg hover:-translate-y-1'}
              `}>
                {/* Completion Badge */}
                {done && (
                  <div className="absolute top-3 right-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}

                {/* Icon Bubble */}
                <div className={`
                  w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110
                  ${done 
                    ? 'bg-green-500' 
                    : `bg-gradient-to-br ${step.color}`}
                `}>
                  <Icon className="w-7 h-7 text-white" />
                </div>

                {/* Content */}
                <h3 className={`font-semibold text-lg mb-1 ${done ? 'text-green-700' : 'text-slate-900'}`}>
                  {step.label}
                </h3>
                <p className={`text-sm ${done ? 'text-green-600' : 'text-slate-500'}`}>
                  {done ? 'Completed' : step.description}
                </p>

                {/* Hover Arrow */}
                <div className={`
                  absolute bottom-4 right-4 w-8 h-8 rounded-full flex items-center justify-center
                  transition-all duration-300 opacity-0 group-hover:opacity-100
                  ${done ? 'bg-green-100' : 'bg-amber-100'}
                `}>
                  <svg className={`w-4 h-4 ${done ? 'text-green-600' : 'text-amber-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Tip Card */}
      <div className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 mb-1">Tip of the Day</h4>
            <p className="text-sm text-slate-600">
              The best biotech founders combine deep scientific expertise with business acumen. 
              Consider finding a cofounder who complements your skills!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AspiringDashboard() {
  return <AspiringLayout>{({ user }) => <DashboardContent user={user} />}</AspiringLayout>;
}
